import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { LANDING_URL } from './lib/service-registry';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { Statistics, Employee, RegionDistribution, ApiError } from './services/api';
import { DemoProvider, useDemo } from './demo/DemoContext';
import { useApiService } from './hooks/useApiService';
import { useEffectiveRole } from './hooks/useEffectiveRole';
import logger from './utils/logger';
import PageHeader from './components/ui/PageHeader';
import MobileTabBar from './components/MobileTabBar';
import AccessDeniedView from './components/AccessDeniedView';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ErrorView from './components/ErrorView';
import LoginPage from './components/LoginPage';
import NoEmployeeRecord from './components/NoEmployeeRecord';
import ToastContainer from './components/ToastContainer';
import StepUpModal from './components/StepUpModal';
import { useToast } from './hooks/useToast';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { View, VIEW_TO_PATH, PATH_TO_VIEW, MENU_ROLES, VIEW_TITLES } from './routes';

// Lazy-loaded page components (code splitting)
const Dashboard = lazy(() => import('./components/Dashboard'));
const MyPage = lazy(() => import('./components/MyPage'));
const WorkflowManagement = lazy(() => import('./components/WorkflowManagement'));
const OrgManagement = lazy(() => import('./components/OrgManagement'));
const HeadcountManagement = lazy(() => import('./components/HeadcountManagement'));
const EvalManagement = lazy(() => import('./components/EvalManagement'));
const TalentDevelopment = lazy(() => import('./components/TalentDevelopment'));
const PromotionManagement = lazy(() => import('./components/PromotionManagement'));
const CompensationManagement = lazy(() => import('./components/CompensationManagement'));
const ComplianceManagement = lazy(() => import('./components/ComplianceManagement'));
const TalentPoolManagement = lazy(() => import('./components/TalentPoolManagement'));
const HRAnalytics = lazy(() => import('./components/HRAnalytics'));
const SystemSettings = lazy(() => import('./components/SystemSettings'));
const PdfUpload = lazy(() => import('./components/PdfUpload'));
const OrganizationChart = lazy(() => import('./components/OrganizationChart'));
const OnboardingTutorial = lazy(() => import('./components/OnboardingTutorial'));
const OvertimeManagement = lazy(() => import('./components/overtime/OvertimeManagement'));
const AttendanceManagement = lazy(() => import('./components/attendance/AttendanceManagement'));
const LeaveManagement = lazy(() => import('./components/leave/LeaveManagement'));
const NoticeBoard = lazy(() => import('./components/notices/NoticeBoard'));
const WeeklyReportManagement = lazy(() => import('./components/weekly-reports/WeeklyReportManagement'));

interface AppError {
  message: string;
  code: string;
  statusCode?: number;
  isTimeout: boolean;
  isNetworkError: boolean;
}

function AppContent() {
  const { isAuthenticated, isLoading: authLoading, isAccessDenied, isNoEmployeeRecord, ssoUserName, ssoUserEmail, user, logout } = useAuth();
  const { isDemo } = useDemo();
  const apiService = useApiService();
  const effectiveRole = useEffectiveRole();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultView: View = ['admin', 'hr_manager'].includes(effectiveRole) ? 'dashboard' : 'mypage';

  // URL → View: derive currentView from URL path
  const currentView: View = PATH_TO_VIEW[location.pathname] || defaultView;

  // Redirect to default view when at root or unknown path (only after auth)
  useEffect(() => {
    if (isAuthenticated && !authLoading && !PATH_TO_VIEW[location.pathname]) {
      navigate(VIEW_TO_PATH[defaultView], { replace: true });
    }
  }, [isAuthenticated, authLoading, location.pathname, navigate, defaultView]);

  // Navigation helper (replaces useState setter)
  const goToView = useCallback((view: View) => {
    navigate(VIEW_TO_PATH[view]);
  }, [navigate]);

  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [regionDistribution, setRegionDistribution] = useState<RegionDistribution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);

  // TSCORP-410: LV2 step-up modal state
  const [stepUpModalOpen, setStepUpModalOpen] = useState(false);

  // Listen for 402 step-up events emitted by the API interceptor
  useEffect(() => {
    const handler = () => setStepUpModalOpen(true);
    window.addEventListener('tscorp:stepup_required', handler);
    return () => window.removeEventListener('tscorp:stepup_required', handler);
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingChecked = useRef(false);
  const { toasts, removeToast, error: showErrorToast, success: showSuccessToast } = useToast();

  useDocumentTitle(VIEW_TITLES[currentView]);

  useEffect(() => {
    // Skip data loading for users with no employee record — they'll see NoEmployeeRecord screen
    if (!isNoEmployeeRecord) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, isNoEmployeeRecord]);

  // 온보딩 체크 — 세션당 한 번만 실행
  useEffect(() => {
    if (onboardingChecked.current) return;
    onboardingChecked.current = true;
    // localStorage에 완료 기록이 있으면 API 호출 없이 스킵
    if (localStorage.getItem('onboarding_completed') === '1') return;
    apiService.getOnboardingStatus()
      .then((obStatus: any) => {
        if (!obStatus.is_completed && !obStatus.skipped) {
          setShowOnboarding(true);
        }
      })
      .catch((e: unknown) => logger.warn('온보딩 상태 확인 실패', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [stats, employeesData, regionData] = await Promise.all([
        apiService.getStatistics(),
        apiService.getAllEmployees(),
        apiService.getRegionDistribution(),
      ]);
      setStatistics(stats);
      setEmployees(employeesData.employees);
      setRegionDistribution(regionData);

      // 성공 시 토스트 표시 (선택적)
      if (!statistics) {
        showSuccessToast('데이터 로딩 완료', '최신 인력 정보를 불러왔습니다');
      }

    } catch (err) {
      logger.error('Error loading data:', err);

      // ApiError 타입 체크 및 에러 정보 파싱
      let appError: AppError;

      if (err instanceof ApiError) {
        appError = {
          message: err.message,
          code: err.code,
          statusCode: err.statusCode,
          isTimeout: err.code === 'TIMEOUT',
          isNetworkError: err.code === 'NETWORK_ERROR'
        };
      } else if (err instanceof Error) {
        appError = {
          message: err.message,
          code: 'UNKNOWN_ERROR',
          isTimeout: false,
          isNetworkError: false
        };
      } else {
        appError = {
          message: '알 수 없는 오류가 발생했습니다',
          code: 'UNKNOWN_ERROR',
          isTimeout: false,
          isNetworkError: false
        };
      }

      setError(appError);

      // 에러 타입별 토스트 메시지
      if (appError.isTimeout) {
        showErrorToast(
          '서버 응답 시간 초과',
          '서버가 응답하지 않습니다. 잠시 후 다시 시도해주세요.',
          10000
        );
      } else if (appError.isNetworkError) {
        showErrorToast(
          '네트워크 오류',
          'API 서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.',
          10000
        );
      } else {
        showErrorToast(
          '데이터 로딩 실패',
          appError.message,
          7000
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const hasAccess = MENU_ROLES[currentView]?.includes(effectiveRole) ?? false;

  // 인증 로딩 중
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
        <div className="spinner" />
      </div>
    );
  }

  // 인증됐지만 HR 시스템 접근 권한 없음 → 접근 거부 페이지 (SSO 리다이렉트 금지)
  // MYB-279: This check MUST come before !isAuthenticated.
  // When the backend returns 403, AuthContext sets isAccessDenied=true but
  // leaves user=null (so isAuthenticated=false). Checking !isAuthenticated
  // first would incorrectly route the user to LoginPage instead of
  // AccessDeniedView.
  if (isAccessDenied) {
    return (
      <AccessDeniedView
        reason="no_organization"
        onGoBack={() => {
          window.location.href = LANDING_URL;
        }}
      />
    );
  }

  // 미인증 → 로그인 페이지
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // TSCORP-485: SSO 인증 성공했지만 HR 인사 프로필 미등록 → 친화적 안내 화면
  const adminBypassRoles = ['admin', 'hr_manager'];
  const userRole = user?.role ?? '';
  if (isNoEmployeeRecord && !adminBypassRoles.includes(userRole)) {
    return (
      <NoEmployeeRecord
        userName={ssoUserName || undefined}
        userEmail={ssoUserEmail || undefined}
      />
    );
  }

  return (
    <ToastProvider>
    <div className="app-container">
      {showOnboarding && <OnboardingTutorial onComplete={() => setShowOnboarding(false)} onNavigate={(v) => { setShowOnboarding(false); goToView(v as View); }} />}
      <Header user={user} onLogout={logout} onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="app-body">
        <Sidebar
          currentView={currentView}
          setCurrentView={(v) => { goToView(v); setSidebarOpen(false); }}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          user={user}
          onLogout={logout}
        />
        <main key={isDemo ? 'demo' : 'real'} className="app-main" role="main" aria-label="메인 콘텐츠">
          {loading ? (
            <div className="loading-container" role="status" aria-live="polite" aria-label="데이터 로딩 중">
              <div className="spinner" aria-hidden="true"></div>
              <span className="sr-only">데이터를 불러오는 중입니다</span>
            </div>
          ) : error ? (
            <ErrorView
              error={error}
              onRetry={loadData}
              title="데이터를 불러오는 중 문제가 발생했습니다"
            />
          ) : !hasAccess ? (
            <AccessDeniedView onGoBack={() => goToView(defaultView)} />
          ) : (
            <Suspense fallback={<div className="loading-container"><div className="spinner" /></div>}>
              {currentView === 'dashboard' && statistics && (
                <Dashboard statistics={statistics} regionDistribution={regionDistribution || undefined} employees={employees} />
              )}
              {currentView === 'mypage' && (
                <MyPage employees={employees} />
              )}
              {currentView === 'workflow' && (
                <WorkflowManagement />
              )}
              {currentView === 'org_mgmt' && (
                <OrgManagement onUploadSuccess={() => {
                  loadData();
                  showSuccessToast('업로드 완료', '직원 정보가 추가되었습니다');
                }} />
              )}
              {currentView === 'recruitment' && (
                <HeadcountManagement />
              )}
              {currentView === 'eval_mgmt' && (
                <EvalManagement />
              )}
              {currentView === 'talent_dev' && (
                <TalentDevelopment />
              )}
              {currentView === 'promotion' && (
                <PromotionManagement />
              )}
              {currentView === 'compensation' && (
                <CompensationManagement />
              )}
              {currentView === 'compliance' && (
                <ComplianceManagement />
              )}
              {currentView === 'talent_pool' && (
                <TalentPoolManagement />
              )}
              {currentView === 'hr_analytics' && (
                <HRAnalytics />
              )}
              {currentView === 'system_settings' && (
                <SystemSettings />
              )}
              {currentView === 'upload' && (
                <div style={{ width: '100%' }}>
                  <PageHeader title="직원 등록" subtitle="파일 업로드 또는 직접 입력으로 새 직원을 등록하세요" />
                  <PdfUpload onUploadSuccess={() => {
                    loadData();
                    showSuccessToast('업로드 완료', '직원 정보가 추가되었습니다');
                  }} />
                </div>
              )}
              {currentView === 'organization' && (
                <OrganizationChart />
              )}
              {currentView === 'overtime' && (
                <OvertimeManagement />
              )}
              {currentView === 'attendance' && (
                <AttendanceManagement />
              )}
              {currentView === 'leave' && (
                <LeaveManagement />
              )}
              {currentView === 'notice_board' && (
                <NoticeBoard />
              )}
              {currentView === 'weekly_report' && (
                <WeeklyReportManagement />
              )}
            </Suspense>
          )}
        </main>
      </div>

      <MobileTabBar
        currentView={currentView}
        setCurrentView={(v) => { goToView(v); setSidebarOpen(false); }}
        onMoreClick={() => setSidebarOpen(true)}
      />

      {/* 전역 토스트 컨테이너 */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
      {/* TSCORP-410: LV2 step-up authentication modal */}
      <StepUpModal
        isOpen={stepUpModalOpen}
        onClose={() => setStepUpModalOpen(false)}
      />
    </div>
    </ToastProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <DemoProvider>
            <AppContent />
          </DemoProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
